import { Injectable, Injector, inject } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpResponse,
} from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { KeycloakService } from '../auth/keycloak.service';

interface CacheEntry {
  response: HttpResponse<any>;
  expiresAt: number;
  url: string;
}

@Injectable()
export class CacheInterceptor implements HttpInterceptor {
  private cache = new Map<string, CacheEntry>();
  private readonly maxCacheSize = 100;
  private readonly injector = inject(Injector);

  /**
   * Who is asking: cached responses are only ever served back to the same user
   * and acting posting. Without this, a second user signing in on the same tab
   * (the SPA is not reloaded on a 401-driven logout) could be served the first
   * user's cached lists, dashboards and permissions.
   */
  private identity(): string {
    try {
      const ctx: any = this.injector.get(KeycloakService).getUserContext();
      return `${ctx?.userId ?? ''}|${ctx?.nodeCd ?? ''}`;
    } catch {
      return '';
    }
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Only cache GET requests
    if (req.method !== 'GET') {
      // Invalidate related cache on mutation
      if (['POST', 'PUT', 'DELETE', 'PATCH'].includes(req.method)) {
        this.invalidateRelatedCache(req.url);
      }
      return next.handle(req);
    }

    // Skip caching for SSE and auth endpoints
    if (this.shouldSkipCache(req.url)) {
      return next.handle(req);
    }

    const cacheKey = this.buildCacheKey(req);
    const cached = this.cache.get(cacheKey);

    if (cached && cached.expiresAt > Date.now()) {
      return of(cached.response.clone());
    }

    // Remove expired entry
    if (cached) {
      this.cache.delete(cacheKey);
    }

    return next.handle(req).pipe(
      tap((event) => {
        if (event instanceof HttpResponse && event.status === 200) {
          this.addToCache(cacheKey, event, req.url);
        }
      })
    );
  }

  private buildCacheKey(req: HttpRequest<any>): string {
    return `${this.identity()}::${req.urlWithParams}`;
  }

  private getTtl(url: string): number {
    const config = environment.cacheConfig;

    if (url.includes('/dashboard/')) {
      return config.dashboardTtl;
    }
    if (url.includes('/budget/summary/')) {
      return config.dashboardTtl;
    }
    if (url.includes('/list') || url.includes('/all')) {
      return config.listTtl;
    }
    if (url.includes('/accounting/coa')) {
      return config.masterDataTtl;
    }
    if (url.includes('/auth/permissions')) {
      return 300000; // 5 min
    }
    return config.listTtl;
  }

  private shouldSkipCache(url: string): boolean {
    return (
      url.includes('/sse/') ||
      url.includes('/auth/login') ||
      url.includes('/auth/logout') ||
      url.includes('/auth/refresh') ||
      url.includes('/dsc-snapshot') ||
      url.includes('/budget-allocation/voucher') ||
      // Posting a schedule line mutates .../:id/interest/:n/post, which no cached .../:id/schedule or list entry sits under, so they must always be read live.
      url.includes('/cash/fixed-deposits')
    );
  }

  private addToCache(key: string, response: HttpResponse<any>, url: string): void {
    // LRU eviction
    if (this.cache.size >= this.maxCacheSize) {
      const firstKey = this.cache.keys().next().value;
      if (firstKey) {
        this.cache.delete(firstKey);
      }
    }

    this.cache.set(key, {
      response: response.clone(),
      expiresAt: Date.now() + this.getTtl(url),
      url,
    });
  }


  private invalidateRelatedCache(mutationUrl: string): void {
    const basePath = mutationUrl.split('?')[0];
    const segments = basePath.split('/');
    const parentPath = segments.slice(0, -1).join('/');
    const collectionPath = segments.slice(0, -2).join('/');

    const keysToDelete: string[] = [];
    this.cache.forEach((entry, key) => {
      if (entry.url.startsWith(parentPath) || (collectionPath && entry.url.startsWith(collectionPath))) {
        keysToDelete.push(key);
      }
    });

    if (/\/cash\/receipts\/[^/]+\/post$/.test(basePath)) {
      this.cache.forEach((entry, key) => {
        if (entry.url.includes('/budget/')) {
          keysToDelete.push(key);
        }
      });
    }

    if (basePath.includes('/workflow-log/') || basePath.includes('/workflow/')) {
      this.cache.forEach((entry, key) => {
        if (
          entry.url.includes('/budget/') ||
          entry.url.includes('/hr/') ||
          entry.url.includes('/cpf/') ||
          entry.url.includes('/advances/') ||
          entry.url.includes('/cash/')
        ) {
          keysToDelete.push(key);
        }
      });
    }

    keysToDelete.forEach((key) => this.cache.delete(key));
  }
}
